import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, HelpCircle, Zap, Target } from "lucide-react";

const CASUALTY_QUOTES = [
  "Listed Microsoft Word as a top skill. In 2026. We\u2019re praying for you.",
  "Three pages long. One page of actual experience. The rest? Hopes and dreams.",
  "Objective statement said \u2018looking for a challenging opportunity.\u2019 Challenge accepted \u2014 getting this hired.",
  "Job hopped six times in two years and somehow listed \u2018team player\u2019 as a strength.",
  "Used the phrase \u2018detail oriented\u2019 and then misspelled their own job title."
];

const HomePage = () => {
  const navigate = useNavigate();
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setQuoteIndex((prev) => (prev + 1) % CASUALTY_QUOTES.length);
        setFadeIn(true);
      }, 400);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background image with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1772631653221-9c77e6f82c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBkYXJrfGVufDB8fHx8MTc3NjIwMTg2OHww&ixlib=rb-4.1.0&q=85')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/90 to-[#050505]" />
      
      {/* Grid overlay */}
      <div className="absolute inset-0 grid-overlay pointer-events-none" />
      
      {/* Noise texture */}
      <div className="absolute inset-0 noise-texture pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Nav */}
        <nav className="w-full py-6 px-6 md:px-12 border-b border-white/10 bg-black/40 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Flame className="w-8 h-8 text-[#FF3B30]" />
              <span className="font-clash text-2xl font-bold tracking-tight">
                TORCHED<span className="text-[#FF3B30]">.CV</span>
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8 text-sm text-[#888888]">
              <span className="tracking-widest uppercase">The Truth Hurts</span>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <main className="flex-1 flex items-center justify-center px-6 md:px-12 py-12 md:py-24">
          <div className="max-w-5xl mx-auto text-center">
            {/* Logo */}
            <div className="mb-8 animate-fade-in">
              <h1 className="font-clash text-6xl sm:text-7xl lg:text-8xl font-black tracking-tighter uppercase">
                TORCHED<span className="text-[#FF3B30]">.CV</span>
              </h1>
            </div>

            {/* Tagline */}
            <p 
              className="text-[#F5F5F5] text-lg sm:text-xl md:text-2xl font-medium mb-10 animate-slide-up max-w-2xl mx-auto leading-relaxed"
              style={{ animationDelay: '0.1s' }}
            >
              Finally get your career on track — or just let us <span className="text-[#FF3B30] font-bold">absolutely destroy</span> your resume. Either works.
            </p>

            {/* Today's Casualty Carousel */}
            <div 
              className="mb-12 max-w-xl mx-auto animate-slide-up"
              style={{ animationDelay: '0.2s' }}
              data-testid="casualty-carousel"
            >
              <div className="border border-[#FF3B30]/30 bg-[#0A0A0A] p-6 relative">
                <p className="font-clash font-bold text-sm uppercase tracking-widest text-[#FF3B30] mb-4 text-center">
                  Today's Casualty &#9760;&#65039;
                </p>
                <div className="min-h-[60px] flex items-center justify-center">
                  <p
                    className={`font-mono text-sm text-[#F5F5F5] leading-relaxed text-center italic transition-opacity duration-300 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
                    data-testid="casualty-quote"
                  >
                    &ldquo;{CASUALTY_QUOTES[quoteIndex]}&rdquo;
                  </p>
                </div>
                <p className="text-[#888888] text-xs text-center mt-3 tracking-wide">
                  — A Brave Soul
                </p>
              </div>
            </div>

            {/* Path buttons - Help is hero, Roast is secondary */}
            <div 
              className="flex flex-col items-center gap-8 animate-slide-up"
              style={{ animationDelay: '0.3s' }}
            >
              {/* Actually Help Me - HERO Option */}
              <button
                data-testid="help-me-btn"
                onClick={() => navigate('/help')}
                className="group relative bg-[#32D74B] text-black font-clash font-bold text-xl uppercase tracking-widest px-14 py-7 transition-all duration-150 hover:bg-[#3DE055] hover:-translate-x-1 hover:-translate-y-1 active:translate-x-1 active:translate-y-1"
                style={{
                  boxShadow: '6px 6px 0px white'
                }}
                onMouseEnter={(e) => e.target.style.boxShadow = '8px 8px 0px white'}
                onMouseLeave={(e) => e.target.style.boxShadow = '6px 6px 0px white'}
              >
                {/* Recommended badge */}
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-black text-[10px] font-bold tracking-widest px-4 py-1 uppercase">
                  Recommended
                </span>
                <span className="flex items-center gap-3">
                  <Target className="w-6 h-6" />
                  Actually Help Me
                </span>
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 w-full max-w-xs">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[#888888] text-xs tracking-widest uppercase">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Roast Me Button - Secondary */}
              <button
                data-testid="roast-me-btn"
                onClick={() => navigate('/roast')}
                className="group bg-transparent text-white font-medium text-base tracking-wide px-8 py-4 border border-[#FF3B30]/50 transition-all duration-200 hover:border-[#FF3B30] hover:bg-[#FF3B30]/10"
              >
                <span className="flex items-center gap-3">
                  <Flame className="w-5 h-5 text-[#FF3B30]" />
                  <span>Just Destroy My Resume</span>
                  <span className="text-[#888888] text-sm">(it's free)</span>
                </span>
              </button>
            </div>

            {/* Features */}
            <div 
              className="mt-24 grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto animate-slide-up"
              style={{ animationDelay: '0.5s' }}
            >
              {/* Help Mode - Larger, more prominent */}
              <div className="text-left p-8 bg-[#111111] border-2 border-[#32D74B]/30 relative sm:col-span-2">
                <div className="absolute -top-3 left-6 bg-[#32D74B] text-black text-[10px] font-bold tracking-widest px-3 py-1 uppercase">
                  Start Here
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <HelpCircle className="w-6 h-6 text-[#32D74B]" />
                  <span className="font-clash font-semibold uppercase tracking-wide text-base">Help Mode</span>
                </div>
                <p className="text-[#F5F5F5] text-sm leading-relaxed">
                  Get genuinely useful, actionable feedback tailored to your career goals. Honest, but helpful. Simply answer the following questions about what you want out of your career, optionally add your LinkedIn profile details, and our AI agents will go to work for you — goal setting and helping you get the career you deserve.
                </p>
              </div>
              {/* Roast Mode - Smaller, secondary */}
              <div className="text-left p-6 bg-[#111111] border border-white/10 sm:col-span-2 sm:max-w-md sm:mx-auto">
                <div className="flex items-center gap-3 mb-3">
                  <Zap className="w-5 h-5 text-[#FF3B30]" />
                  <span className="font-clash font-semibold uppercase tracking-wide text-sm">Roast Mode</span>
                </div>
                <p className="text-[#888888] text-sm leading-relaxed">
                  Your resume deserves the truth. And the truth hurts. Get a savage, hilarious roast from an AI that's equal parts Gordon Ramsay and passive-aggressive HR.
                </p>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-6 px-6 md:px-12 border-t border-white/10">
          <div className="max-w-7xl mx-auto flex items-center justify-center text-[#888888] text-xs tracking-widest uppercase">
            <span>Powered by AI. Fueled by disappointment.</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
